import React from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import { theme } from '../style/theme';
import { vitalSignDescriptions, VitalSignDescription } from '../data/vitalSignDescriptions';

// Content sections
const Section = styled.div`
  margin-bottom: ${theme.spacing[5]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.neutral.gray800};
  margin: 0 0 ${theme.spacing[3]} 0;
  line-height: ${theme.typography.lineHeight.tight};
`;

const SectionContent = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.neutral.gray600};
  line-height: ${theme.typography.lineHeight.normal};
  
  p {
    margin: 0 0 ${theme.spacing[3]} 0;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

// Abbreviation and full name header
const VitalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: linear-gradient(135deg, ${theme.colors.primary.main}15, ${theme.colors.secondary.main}15);
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.neutral.gray200};
`;

const AbbreviationBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  height: 60px;
  background: ${theme.colors.primary.gradient};
  color: ${theme.colors.neutral.white};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
`;

const VitalInfo = styled.div`
  flex: 1;
`;

const FullName = styled.div`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.neutral.gray800};
  margin-bottom: ${theme.spacing[1]};
`;

const NormalRange = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral.gray600};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-bottom: ${theme.spacing[1]};
  
  span {
    color: ${theme.colors.primary.main};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const CurrentReading = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.neutral.gray800};
  font-weight: ${theme.typography.fontWeight.semibold};
  
  .label {
    color: ${theme.colors.neutral.gray600};
    font-weight: ${theme.typography.fontWeight.medium};
    margin-right: ${theme.spacing[2]};
  }
  
  .value {
    color: ${theme.colors.primary.main};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.lg};
  }
  
  .unit {
    color: ${theme.colors.neutral.gray500};
    font-weight: ${theme.typography.fontWeight.medium};
    font-size: ${theme.typography.fontSize.sm};
    margin-left: ${theme.spacing[1]};
  }
`;

// Clinical significance section
const ClinicalGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing[3]};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ClinicalCard = styled.div<{ type: 'high' | 'low' }>`
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${props => 
    props.type === 'high' 
      ? theme.colors.status.warning 
      : theme.colors.status.info
  };
  background: ${props => 
    props.type === 'high' 
      ? `${theme.colors.status.warning}08`
      : `${theme.colors.status.info}08`
  };
`;

const ClinicalTitle = styled.div<{ type: 'high' | 'low' }>`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${props => 
    props.type === 'high' 
      ? theme.colors.status.warning 
      : theme.colors.status.info
  };
  margin-bottom: ${theme.spacing[2]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ClinicalText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral.gray700};
  line-height: ${theme.typography.lineHeight.normal};
`;

// Factors list
const FactorsList = styled.ul`
  margin: 0;
  padding-left: ${theme.spacing[5]};
  
  li {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.neutral.gray600};
    line-height: ${theme.typography.lineHeight.normal};
    margin-bottom: ${theme.spacing[2]};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

// Warning section
const WarningSection = styled.div`
  padding: ${theme.spacing[4]};
  background: ${theme.colors.status.warning}10;
  border: 1px solid ${theme.colors.status.warning};
  border-radius: ${theme.borderRadius.lg};
  
  &::before {
    content: '⚠️';
    font-size: ${theme.typography.fontSize.lg};
    margin-right: ${theme.spacing[2]};
  }
`;

const WarningText = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral.gray700};
  line-height: ${theme.typography.lineHeight.normal};
  display: inline;
`;

interface VitalSignModalProps {
  isOpen: boolean;
  onClose: () => void;
  vitalSignKey: string | null;
  currentValue?: string | number;
  unit?: string;
}

const VitalSignModal: React.FC<VitalSignModalProps> = ({ 
  isOpen, 
  onClose, 
  vitalSignKey,
  currentValue,
  unit
}) => {
  if (!vitalSignKey || !vitalSignDescriptions[vitalSignKey]) {
    return null;
  }

  const vital: VitalSignDescription = vitalSignDescriptions[vitalSignKey];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      maxWidth="700px"
    >
      {/* Header with abbreviation and name */}
      <VitalHeader>
        <AbbreviationBadge>
          {vital.abbreviation}
        </AbbreviationBadge>
        <VitalInfo>
          <FullName>{vital.fullName}</FullName>
          <NormalRange>
            Normal Range: <span>{vital.normalRange.text}</span>
          </NormalRange>
          {currentValue !== undefined && currentValue !== null && currentValue !== '--' && currentValue !== 'N/A' && (
            <CurrentReading>
              <span className="label">Your Reading:</span>
              <span className="value">{currentValue}</span>
              {unit && <span className="unit">{unit}</span>}
            </CurrentReading>
          )}
        </VitalInfo>
      </VitalHeader>

      {/* Description */}
      <Section>
        <SectionTitle>What This Measures</SectionTitle>
        <SectionContent>
          <p>{vital.description}</p>
          <p>{vital.whatItMeans}</p>
        </SectionContent>
      </Section>

      {/* Clinical Significance */}
      {(vital.clinicalSignificance.high || vital.clinicalSignificance.low) && (
        <Section>
          <SectionTitle>Clinical Significance</SectionTitle>
          <ClinicalGrid>
            {vital.clinicalSignificance.high && (
              <ClinicalCard type="high">
                <ClinicalTitle type="high">High Values</ClinicalTitle>
                <ClinicalText>{vital.clinicalSignificance.high}</ClinicalText>
              </ClinicalCard>
            )}
            {vital.clinicalSignificance.low && (
              <ClinicalCard type="low">
                <ClinicalTitle type="low">Low Values</ClinicalTitle>
                <ClinicalText>{vital.clinicalSignificance.low}</ClinicalText>
              </ClinicalCard>
            )}
          </ClinicalGrid>
        </Section>
      )}

      {/* Factors Affecting */}
      <Section>
        <SectionTitle>Factors That Can Affect This Measurement</SectionTitle>
        <SectionContent>
          <FactorsList>
            {vital.factorsAffecting.map((factor, index) => (
              <li key={index}>{factor}</li>
            ))}
          </FactorsList>
        </SectionContent>
      </Section>

      {/* When to be concerned */}
      {vital.whenToConcern && (
        <Section>
          <WarningSection>
            <WarningText>
              <strong>When to Consult a Healthcare Provider: </strong>
              {vital.whenToConcern}
            </WarningText>
          </WarningSection>
        </Section>
      )}
    </Modal>
  );
};

export default VitalSignModal;
